import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ReactFlow, ReactFlowProvider, Background, Controls, MiniMap, Handle, Position, MarkerType, useReactFlow, useUpdateNodeInternals } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import './EngineeringFlow.css';

// Engineering Handbook diagram renderer. Takes one spec from
// data/engineeringDiagrams.js (nodes on a col/row grid + labelled edges) and
// renders it with React Flow; clicking a node opens the detail rail (what it
// is, files, live-config keys, brain pages). Pure presentation — nothing is
// editable, nothing is persisted.

const COL_W = 250;
const ROW_H = 120;
const NODE_W = 196;

export const KIND_LABEL = {
  client: 'Client', store: 'Store', service: 'Service', worker: 'Worker', provider: 'Provider',
  gate: 'Gate', effect: 'Effect', host: 'Host', external: 'External', doc: 'Definition', job: 'Task',
};

const EDGE_STYLE = {
  data: { stroke: 'rgba(var(--fg-rgb),0.45)' },
  realtime: { stroke: '#14e0c8' },
  queue: { stroke: 'rgba(var(--fg-rgb),0.5)', strokeDasharray: '6 4' },
  gate: { stroke: '#e05252' },
  control: { stroke: 'rgba(var(--fg-rgb),0.3)', strokeDasharray: '3 4' },
};

function HandbookNode({ data, selected }) {
  return (
    <div className={`ef-node is-${data.kind} ${selected ? 'is-selected' : ''}`}>
      <Handle type="target" position={Position.Left} className="ef-handle" />
      <Handle type="target" position={Position.Top} id="t" className="ef-handle" />
      <span className="ef-node-kind">{KIND_LABEL[data.kind] || data.kind}</span>
      <span className="ef-node-label">{data.label}</span>
      {data.files?.length ? <span className="ef-node-files">{data.files.length} file{data.files.length === 1 ? '' : 's'}</span> : null}
      <Handle type="source" position={Position.Right} className="ef-handle" />
      <Handle type="source" position={Position.Bottom} id="b" className="ef-handle" />
    </div>
  );
}

const NODE_TYPES = { handbook: HandbookNode };

// On a hard page load the first node measurement can land before layout
// settles (fonts, the grid), and nothing re-triggers it — nodes show but
// edges and fitView never arrive until something resizes. Re-measure the
// handles and fit once the frame is stable.
function SettleOnMount({ nodeIds }) {
  const { fitView } = useReactFlow();
  const updateNodeInternals = useUpdateNodeInternals();
  useEffect(() => {
    const kick = () => { updateNodeInternals(nodeIds); fitView({ padding: 0.15 }); };
    const t1 = setTimeout(kick, 80);
    const t2 = setTimeout(kick, 500);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [nodeIds, fitView, updateNodeInternals]);
  return null;
}

function toFlow(spec) {
  const nodes = spec.nodes.map((nd) => ({
    id: nd.id,
    type: 'handbook',
    position: { x: nd.col * COL_W, y: nd.row * ROW_H },
    data: nd,
    style: { width: NODE_W },
    draggable: false,
  }));
  const pos = Object.fromEntries(spec.nodes.map((nd) => [nd.id, nd]));
  const edges = spec.edges.map((ed, i) => {
    const a = pos[ed.from];
    const b = pos[ed.to];
    const vertical = a && b && a.col === b.col;
    const backwards = a && b && b.col < a.col;
    return {
      id: `${ed.from}-${ed.to}-${i}`,
      source: ed.from,
      target: ed.to,
      sourceHandle: vertical ? 'b' : undefined,
      targetHandle: vertical ? 't' : undefined,
      label: ed.label || undefined,
      type: backwards ? 'smoothstep' : 'default',
      animated: ed.kind === 'realtime',
      style: EDGE_STYLE[ed.kind] || EDGE_STYLE.data,
      labelStyle: { fill: 'var(--muted)', fontSize: 10.5 },
      labelBgStyle: { fill: 'var(--bg)', fillOpacity: 0.9 },
      labelBgPadding: [4, 2],
      markerEnd: { type: MarkerType.ArrowClosed, color: (EDGE_STYLE[ed.kind] || EDGE_STYLE.data).stroke, width: 14, height: 14 },
    };
  });
  return { nodes, edges };
}

export function NodeDetail({ node, sectionHref, onClose }) {
  if (!node) return null;
  return (
    <aside className="ef-detail">
      <div className="ef-detail-head">
        <span className={`ef-kind is-${node.kind}`}>{KIND_LABEL[node.kind] || node.kind}</span>
        <strong>{node.label}</strong>
        {onClose && <button type="button" className="ef-close" onClick={onClose} aria-label="Close">×</button>}
      </div>
      {node.what && <p className="ef-detail-what">{node.what}</p>}
      {node.files?.length ? (
        <>
          <h4>Files</h4>
          <ul className="ef-list">{node.files.map((f) => <li key={f}><code>{f}</code></li>)}</ul>
        </>
      ) : null}
      {node.config?.length ? (
        <>
          <h4>Live config</h4>
          <ul className="ef-list">{node.config.map((k) => <li key={k}><Link to={`/admin/engineering/reference/liveConfigKeys?q=${encodeURIComponent(k)}`}><code>{k}</code></Link></li>)}</ul>
        </>
      ) : null}
      {node.brain?.length ? (
        <>
          <h4>Brain</h4>
          <ul className="ef-list">{node.brain.map((s) => <li key={s}><Link to={`/admin/brain/page/${s}`}>[[{s}]]</Link></li>)}</ul>
        </>
      ) : null}
      {sectionHref && <p className="ef-detail-foot"><Link to={sectionHref}>Read the section →</Link></p>}
    </aside>
  );
}

export default function EngineeringFlow({ spec, height = 520, sectionHref, compact = false }) {
  const { nodes, edges } = useMemo(() => toFlow(spec), [spec]);
  const [selected, setSelected] = useState(null);
  const onNodeClick = useCallback((_e, node) => setSelected((cur) => (cur?.id === node.id ? null : node.data)), []);
  const onPaneClick = useCallback(() => setSelected(null), []);
  const nodeIds = useMemo(() => nodes.map((nd) => nd.id), [nodes]);
  return (
    <div className={`ef-wrap ${selected ? 'has-detail' : ''} ${compact ? 'is-compact' : ''}`} style={{ height }}>
      <div className="ef-canvas">
        <ReactFlowProvider>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={NODE_TYPES}
            fitView
            fitViewOptions={{ padding: 0.15 }}
            minZoom={0.2}
            maxZoom={2.5}
            nodesDraggable={false}
            nodesConnectable={false}
            elementsSelectable
            onNodeClick={onNodeClick}
            onPaneClick={onPaneClick}
            proOptions={{ hideAttribution: true }}
          >
            <Background gap={24} size={1} color="rgba(127,127,127,0.18)" />
            <Controls showInteractive={false} />
            {!compact && <MiniMap pannable zoomable nodeColor={() => 'rgba(127,127,127,0.5)'} maskColor="rgba(0,0,0,0.35)" />}
            <SettleOnMount nodeIds={nodeIds} />
          </ReactFlow>
        </ReactFlowProvider>
      </div>
      {selected && <NodeDetail node={selected} sectionHref={sectionHref} onClose={() => setSelected(null)} />}
    </div>
  );
}

export function FlowLegend() {
  return (
    <div className="ef-legend">
      {Object.entries(KIND_LABEL).map(([k, v]) => <span key={k} className={`ef-kind is-${k}`}>{v}</span>)}
      <span className="ef-legend-sep" />
      {[['data', 'data'], ['realtime', 'realtime'], ['queue', 'queue / claim'], ['gate', 'gate'], ['control', 'control']].map(([k, label]) => (
        <span key={k} className="ef-edge-key">
          <i className={k === 'queue' ? 'is-dashed' : k === 'control' ? 'is-dotted' : ''} style={{ borderTopColor: EDGE_STYLE[k].stroke }} /> {label}
        </span>
      ))}
    </div>
  );
}
